import { useAtom } from "jotai";
import Axios from 'axios';
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IProcessingQueueItem, processingQueueAtom, processingQueueLoadingAtom } from "./types";
import { IOrderQueue } from "../order";

type FetchProcessingQueue = (brandId: number) => void;
type FetchOrderQueueStatus = (order_number: string) => Promise<IOrderQueue | null>;

type ProcessingQueueFunctions = {
    getQueue: FetchProcessingQueue;
    getOrderQueueStatus: FetchOrderQueueStatus;
}

type UseProcessingQueue = [
    IProcessingQueueItem[],
    (payload: IProcessingQueueItem[]) => void,
    ProcessingQueueFunctions
];

// #region useProcessingQueue
export const useProcessingQueue = (): UseProcessingQueue => {
    const [queue, setQueue] = useAtom(processingQueueAtom);
    const [, setLoading] = useAtom(processingQueueLoadingAtom);

    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    // #region getQueue
    const getQueue: FetchProcessingQueue = (brandId: number) => {
        const fetchQueue = async (setter: (payload: IProcessingQueueItem[]) => void): Promise<void> => {
            setLoading(true);
            const result = await Axios.get(`${environment.queuesUrl}/image-processor?brand_id=${brandId}`, {
                headers: {
                    'X-Wallmates-Auth': authToken
                }
            }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
            if (result) {
                setter(result.data);
            } else {
                setSnackbar({
                    show: true,
                    snackbarLevel: 'error',
                    text: 'There was an error while getting processing queue. Please try again later.'
                });
            }
            setLoading(false);
        };
        fetchQueue(setQueue);
    };

    // #region getOrderQueueStatus
    const getOrderQueueStatus: FetchOrderQueueStatus = async (order_number: string) => {
        setLoading(true);
        const result = await Axios.get(`${environment.ordersUrl}/order-status?order_number=${order_number}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (result) {
            setLoading(false);
            return result.data;
        } else {
            setLoading(false);
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while getting Order Queue Status. Please try again later.'
            });
            return null;
        }
    };

    const keyFunctions: ProcessingQueueFunctions = {
        getQueue,
        getOrderQueueStatus
    };

    return [
        queue,
        setQueue,
        keyFunctions
    ];
};
